import { useMemo } from 'react';

import { filterRows, isRowApproved } from './approvalQueueModel';
import type { ChecklistStatus } from './shared/checklistUtils';
import { getBlackoutSearchText } from './tabs/blackoutDates/blackoutQueue';
import { getBundledExperienceSearchText } from './tabs/bundledExperiences/bundledExperiencesQueue';
import { getCampaignSearchText } from './tabs/campaign/campaignQueue';
import { getDealOptionsInclusionsSearchText } from './tabs/dealOptionsInclusions/dealOptionsInclusionsRules';
import { getDiscountsMarginSearchText } from './tabs/discountsMargin/discountsMarginQueue';
import { getCapacitySearchText, getMarginSearchText } from './tabs/egs/egsQueue';
import { getSetupSearchText } from './tabs/setup/setupQueue';
import { getSurchargeTableSearchText, getSurchargesSearchText } from './tabs/surcharges/surchargesQueue';
import { getTaxesPaymentsSearchText } from './tabs/taxesPayments/taxesPaymentsQueue';
import type { ChecklistCategoryId, ChecklistRowsByCategory, ManualOverrideState } from './types';

export interface VisibleChecklistRows {
  visibleBlackoutRows: ChecklistRowsByCategory['blackoutRows'];
  visibleBundledExperienceRows: ChecklistRowsByCategory['bundledExperienceRows'];
  visibleCampaignRows: ChecklistRowsByCategory['campaignRows'];
  visibleCapacityRows: ChecklistRowsByCategory['capacityRows'];
  visibleDiscountsMarginRows: ChecklistRowsByCategory['discountsMarginRows'];
  visibleDealOptionsInclusionsRows: ChecklistRowsByCategory['dealOptionsInclusionsRows'];
  visibleMarginRows: ChecklistRowsByCategory['marginRows'];
  visibleSetupRows: ChecklistRowsByCategory['setupRows'];
  visibleSurchargeRules: ChecklistRowsByCategory['baseSurchargesRows'];
  visibleSurchargeTableRows: ChecklistRowsByCategory['surchargeTableRows'];
  visibleTaxesPaymentsRows: ChecklistRowsByCategory['taxesPaymentsRows'];
}

interface Input extends ChecklistRowsByCategory {
  activeCategoryId: ChecklistCategoryId;
  isApprovalQueueMode: boolean;
  manualOverrides: ManualOverrideState;
  searchQuery: string;
  showAutoApproved: boolean;
}

export function useVisibleChecklistRows({
  activeCategoryId,
  baseSurchargesRows,
  blackoutRows,
  bundledExperienceRows,
  campaignRows,
  capacityRows,
  discountsMarginRows,
  dealOptionsInclusionsRows,
  isApprovalQueueMode,
  manualOverrides,
  marginRows,
  searchQuery,
  setupRows,
  showAutoApproved,
  surchargeTableRows,
  taxesPaymentsRows,
}: Input): VisibleChecklistRows {
  const filterInput = useMemo(
    () => ({
      isApprovalQueueMode,
      manualOverrides,
      searchQuery,
      showAutoApproved,
    }),
    [isApprovalQueueMode, manualOverrides, searchQuery, showAutoApproved],
  );

  const visibleSetupRows = useMemo(
    () =>
      filterChecklistRows<ChecklistRowsByCategory['setupRows'][number]>({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'setup',
        rows: setupRows,
        text: getSetupSearchText,
      }),
    [activeCategoryId, filterInput, setupRows],
  );
  const visibleCampaignRows = useMemo(
    () =>
      filterChecklistRows<ChecklistRowsByCategory['campaignRows'][number]>({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'campaign',
        rows: campaignRows,
        text: getCampaignSearchText,
      }),
    [activeCategoryId, campaignRows, filterInput],
  );
  const visibleDiscountsMarginRows = useMemo(
    () =>
      filterChecklistRows<ChecklistRowsByCategory['discountsMarginRows'][number]>({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'discountsMargin',
        rows: discountsMarginRows,
        text: getDiscountsMarginSearchText,
      }),
    [activeCategoryId, discountsMarginRows, filterInput],
  );
  const visibleMarginRows = useMemo(
    () =>
      filterChecklistRows<ChecklistRowsByCategory['marginRows'][number]>({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'egs',
        rows: marginRows,
        text: getMarginSearchText,
      }),
    [activeCategoryId, filterInput, marginRows],
  );
  const visibleCapacityRows = useMemo(
    () =>
      filterChecklistRows<ChecklistRowsByCategory['capacityRows'][number]>({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'egs',
        rows: capacityRows,
        text: getCapacitySearchText,
      }),
    [activeCategoryId, capacityRows, filterInput],
  );
  const visibleDealOptionsInclusionsRows = useMemo(
    () =>
      filterChecklistRows<ChecklistRowsByCategory['dealOptionsInclusionsRows'][number]>({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'dealOptionsInclusions',
        rows: dealOptionsInclusionsRows,
        text: getDealOptionsInclusionsSearchText,
      }),
    [activeCategoryId, dealOptionsInclusionsRows, filterInput],
  );
  const visibleSurchargeTableRows = useMemo(
    () =>
      filterChecklistRows<ChecklistRowsByCategory['surchargeTableRows'][number]>({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'surcharges',
        rows: surchargeTableRows,
        text: getSurchargeTableSearchText,
      }),
    [activeCategoryId, filterInput, surchargeTableRows],
  );
  const visibleSurchargeRules = useMemo(
    () =>
      filterRows<ChecklistRowsByCategory['baseSurchargesRows'][number]>({
        isApproved: (row) => isRowApproved(row, manualOverrides),
        rows: isApprovalQueueMode ? baseSurchargesRows : [],
        searchQuery,
        showAutoApproved: false,
        text: getSurchargesSearchText,
      }),
    [baseSurchargesRows, isApprovalQueueMode, manualOverrides, searchQuery],
  );
  const visibleTaxesPaymentsRows = useMemo(
    () =>
      filterChecklistRows<ChecklistRowsByCategory['taxesPaymentsRows'][number]>({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'taxesPayments',
        rows: taxesPaymentsRows,
        text: getTaxesPaymentsSearchText,
      }),
    [activeCategoryId, filterInput, taxesPaymentsRows],
  );
  const visibleBlackoutRows = useMemo(
    () =>
      filterChecklistRows<ChecklistRowsByCategory['blackoutRows'][number]>({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'blackoutDates',
        rows: blackoutRows,
        text: getBlackoutSearchText,
      }),
    [activeCategoryId, blackoutRows, filterInput],
  );
  const visibleBundledExperienceRows = useMemo(
    () =>
      filterChecklistRows<ChecklistRowsByCategory['bundledExperienceRows'][number]>({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'bundledExperiences',
        rows: bundledExperienceRows,
        text: getBundledExperienceSearchText,
      }),
    [activeCategoryId, bundledExperienceRows, filterInput],
  );

  return {
    visibleBlackoutRows,
    visibleBundledExperienceRows,
    visibleCampaignRows,
    visibleCapacityRows,
    visibleDiscountsMarginRows,
    visibleDealOptionsInclusionsRows,
    visibleMarginRows,
    visibleSetupRows,
    visibleSurchargeRules,
    visibleSurchargeTableRows,
    visibleTaxesPaymentsRows,
  };
}

function filterChecklistRows<Row extends { id: string; status: ChecklistStatus }>({
  isActiveCategory,
  isApprovalQueueMode,
  manualOverrides,
  rows,
  searchQuery,
  showAutoApproved,
  text,
}: {
  isActiveCategory: boolean;
  isApprovalQueueMode: boolean;
  manualOverrides: ManualOverrideState;
  rows: Array<Row>;
  searchQuery: string;
  showAutoApproved: boolean;
  text: (row: Row) => string;
}) {
  return filterRows<Row>({
    isApproved: (row) => isRowApproved(row, manualOverrides),
    rows: isActiveCategory || isApprovalQueueMode ? rows : [],
    searchQuery,
    showAutoApproved: isApprovalQueueMode ? false : showAutoApproved,
    text,
  });
}
